import { useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { FileText, Upload, Download, FileImage, File, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui'
import { useDocuments } from '@/hooks/useDocuments'

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const getFileIcon = (mimeType: string) => {
  if (mimeType.includes('pdf')) return <FileText size={24} className="text-red-400" />
  if (mimeType.includes('image')) return <FileImage size={24} className="text-blue-400" />
  return <File size={24} className="text-slate-400" />
}

export function DocumentsPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const { documents, isLoading, error, uploadDocument, getDownloadUrl } = useDocuments(businessId)

  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [docType, setDocType] = useState('other')
  const [notes, setNotes] = useState('')

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0])
      setUploadError(null)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    setIsUploading(true)
    setUploadError(null)

    const res = await uploadDocument(selectedFile, docType, notes)
    if (res.success) {
      setSelectedFile(null)
      setDocType('other')
      setNotes('')
      if (fileInputRef.current) fileInputRef.current.value = ''
    } else {
      setUploadError(res.error || 'Gagal mengunggah file')
    }
    setIsUploading(false)
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p>Memuat daftar dokumen...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-20">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Dokumen Bisnis (Cloud Storage)</h1>
        <p className="text-sm text-slate-400 mt-1">Simpan bukti modal, invoice, dan kontrak perusahaan dengan aman di R2.</p>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
          <p>{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Upload Form */}
        <div className="lg:col-span-1">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm sticky top-6">
            <h2 className="text-lg font-semibold text-slate-200 mb-4">Unggah Dokumen</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Pilih File (Max 5MB, PDF/Img)</label>
                <div className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center hover:bg-slate-800/50 transition-colors">
                  <input 
                    type="file" 
                    className="hidden" 
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                  />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                    <Upload size={16} className="mr-2" /> {selectedFile ? 'Ganti File' : 'Browse File'}
                  </Button>
                  {selectedFile && (
                    <p className="text-xs text-brand-400 mt-2 truncate px-2">{selectedFile.name} ({formatBytes(selectedFile.size)})</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Kategori Dokumen</label>
                <select 
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                >
                  <option value="capital_proof">Bukti Setoran Modal</option>
                  <option value="founder_agreement">Founder Agreement</option>
                  <option value="invoice">Invoice / Tagihan</option>
                  <option value="receipt">Kuitansi / Struk</option>
                  <option value="report_export">Laporan Cetak</option>
                  <option value="other">Lainnya</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Catatan Tambahan (Opsional)</label>
                <textarea 
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200"
                  placeholder="Misal: Bukti transfer modal investor A..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                />
              </div>

              {uploadError && (
                <div className="text-xs text-rose-400 flex items-start gap-1">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <span>{uploadError}</span>
                </div>
              )}

              <Button 
                onClick={handleUpload} 
                disabled={!selectedFile || isUploading}
                className="w-full"
              >
                {isUploading ? 'Mengunggah...' : 'Unggah ke Cloud'}
              </Button>
            </div>
          </div>
        </div>

        {/* Documents List */}
        <div className="lg:col-span-2">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-800">
              <h2 className="text-lg font-semibold text-slate-200">Daftar Dokumen</h2>
            </div>
            
            {documents.length === 0 ? (
              <div className="p-10 text-center text-slate-500">
                <FileText size={48} className="mx-auto mb-3 opacity-20" />
                <p>Belum ada dokumen yang diunggah.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-800/50">
                {documents.map(doc => (
                  <div key={doc.id} className="p-4 hover:bg-slate-800/30 transition-colors flex items-start justify-between gap-4">
                    <div className="flex gap-3 items-start overflow-hidden">
                      <div className="p-2 bg-slate-800 rounded-lg shrink-0">
                        {getFileIcon(doc.mimeType)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-200 truncate" title={doc.fileName}>{doc.fileName}</p>
                        <div className="flex flex-wrap gap-2 text-xs text-slate-500 mt-1">
                          <span className="uppercase tracking-wider px-1.5 py-0.5 bg-slate-800 rounded text-slate-400">
                            {doc.documentType.replace('_', ' ')}
                          </span>
                          <span>{formatBytes(doc.sizeBytes)}</span>
                          <span>•</span>
                          <span>{new Date(doc.createdAt).toLocaleDateString('id-ID')}</span>
                        </div>
                        {doc.notes && <p className="text-xs text-slate-400 mt-2 truncate">{doc.notes}</p>}
                      </div>
                    </div>
                    <a 
                      href={getDownloadUrl(doc.id)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 bg-slate-800 hover:bg-brand-500 hover:text-white text-brand-400 rounded-lg transition-colors shrink-0"
                      title="Download/View File"
                    >
                      <Download size={18} />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}
